import { observer } from 'mobx-react';
import React from 'react';
import b_ from 'b_';

import { Section } from 'controls/react/layout/section';
import { FormEntryNew } from 'controls/react/form/formEntryNew';
import { AntSelect } from 'controls/react/ant/antSelect';
import { AntButton } from 'controls/react/ant/antButton';
import { DeleteOutlined } from '@ant-design/icons';
import { CvColorPicker } from 'controls/react/colorPicker/sketchColorPicker';
import { i18n } from 'core/localization';
import { FieldType } from 'controls/queryBuilder/ruleDefinition';
import { ColorDescriptionType, getColorTypeOptions, getGradientDirectionOptions } from 'components/HighchartsWrapper';
import { DataSourceSelector } from 'controls/designer/features/widgets/datasources/shared/components/data-source-selector/dataSourceSelector';
import { DataSourceColumnSelector } from 'controls/designer/features/widgets/datasources/shared/components/data-source-column-selector/dataSourceColumnSelector';
import { DataSourceRowEditor } from 'controls/designer/features/widgets/datasources/shared/components/data-source-row-editor/dataSourceRowEditor';

import { DataSourceSingleGraphChartType } from '../types';
import { DataSourceSingleGraphRowStore } from './DataSourceSingleGraphRowStore';

const b = b_.with('data-sources-single-graph');

const chartTypeOptions: { value: DataSourceSingleGraphChartType; label: string }[] = [
	{ value: 'line', label: i18n('Line') },
	{ value: 'area', label: i18n('Area') },
	{ value: 'arearange', label: i18n('Range') },
];

export const DataSourceRow = observer(
	(props: {
		row: DataSourceSingleGraphRowStore;
		canRemove: boolean;
		onRemove: (row: DataSourceSingleGraphRowStore) => void;
	}) => {
		const { row, canRemove, onRemove } = props;
		const columnSelectorsDisabled = !row.row.dataSourceId;
		const isGradient = row.row.colorSettings.type == ColorDescriptionType.Gradient;

		return (
			<Section appearance={'none'} direction={'row'} alignItems={'flex-start'} contentClass={b('datasource-row')}>
				<Section appearance={'none'} childrenPadding={true} className={b('datasource-row-content')}>
					<FormEntryNew label={i18n('Datasource')} model={row.row} modelField={'dataSourceId'}>
						<DataSourceSelector manager={row.manager} options={row.dataSourceOptions} />
					</FormEntryNew>
					<Section
						appearance={'none'}
						direction={'row'}
						alignItems={'center'}
						layout={'equal'}
						childrenPadding={false}
						contentClass={b('datasource-row-line')}
					>
						<FormEntryNew label={i18n('Time column')} alwaysWithValue={true}>
							<DataSourceColumnSelector
								manager={row.manager}
								accountId={row.accountId}
								dataSourceId={row.row.dataSourceId}
								requestId={row.requestId}
								fieldType={FieldType.Date}
								value={row.row.timeColumn}
								onChange={(value) => (row.row.timeColumn = value)}
								disabled={columnSelectorsDisabled}
							/>
						</FormEntryNew>
						<FormEntryNew label={i18n('Metric row')} alwaysWithValue={true}>
							<DataSourceColumnSelector
								manager={row.manager}
								accountId={row.accountId}
								dataSourceId={row.row.dataSourceId}
								requestId={row.requestId}
								fieldType={FieldType.Number}
								value={row.row.metricColumn}
								onChange={(value) => (row.row.metricColumn = value)}
								disabled={columnSelectorsDisabled}
							/>
						</FormEntryNew>
						<FormEntryNew label={i18n('Datasource row')} alwaysWithValue={true}>
							<DataSourceRowEditor manager={row.manager} disabled={columnSelectorsDisabled} />
						</FormEntryNew>
					</Section>
					<Section
						appearance={'none'}
						direction={'row'}
						alignItems={'center'}
						layout={'equal'}
						childrenPadding={false}
						contentClass={b('datasource-row-line')}
					>
						<FormEntryNew label={i18n('Chart type')} model={row.row} modelField={'chartType'}>
							<AntSelect options={chartTypeOptions} />
						</FormEntryNew>
						<FormEntryNew label={i18n('Color type')} model={row.row.colorSettings} modelField={'type'}>
							<AntSelect options={getColorTypeOptions()} />
						</FormEntryNew>
						{isGradient && (
							<FormEntryNew label={i18n('Gradient direction')} model={row.row.colorSettings} modelField={'direction'}>
								<AntSelect options={getGradientDirectionOptions()} />
							</FormEntryNew>
						)}
					</Section>
					<Section
						appearance={'none'}
						direction={'row'}
						alignItems={'center'}
						childrenPadding={false}
						contentClass={b('datasource-row-line')}
					>
						<FormEntryNew
							label={i18n(isGradient ? 'Start color' : 'Color')}
							model={row.row.colorSettings}
							modelField={'startColor'}
						>
							<CvColorPicker />
						</FormEntryNew>
						{isGradient && (
							<FormEntryNew label={i18n('End color')} model={row.row.colorSettings} modelField={'finishColor'}>
								<CvColorPicker />
							</FormEntryNew>
						)}
					</Section>
				</Section>
				{canRemove && (
					<AntButton
						type={'text'}
						shape={'circle'}
						icon={<DeleteOutlined />}
						onClick={() => onRemove(row)}
						className={b('datasource-row-delete')}
						title={i18n('Remove')}
					/>
				)}
			</Section>
		);
	},
);
